import type {
  HairPiece,
  KeeperBase,
  KeeperBodyType,
  KeeperData,
  KeeperFaceExtra,
  KeeperFaceLine,
  KeeperFaceShape,
  KeeperHairstyle,
  KeeperSkinTone,
  KeeperSwatch,
  OutfitPalette,
} from '../schemas/data/keepers.js';

/**
 * Keeper bases and palettes (design doc §23). Ids are stored in players'
 * `keepers` rows: add freely, never rename or remove one. Shapes and colours
 * are first guesses to judge in the Keeper gallery (`/keepers.html`).
 */

// TUNE: all hair colours
export const KEEPER_HAIR_COLORS: KeeperSwatch[] = [
  { id: 'cocoa', name: 'Cocoa', color: '#6b4430' },
  { id: 'midnight', name: 'Midnight', color: '#2f2a3d' },
  { id: 'honey', name: 'Honey', color: '#e8b75a' },
  { id: 'ginger', name: 'Ginger', color: '#d9733f' },
  { id: 'berry', name: 'Berry', color: '#d9668f' },
  { id: 'mint', name: 'Mint', color: '#7fd1b9' },
  { id: 'lilac', name: 'Lilac', color: '#b39ddb' },
  { id: 'cloud', name: 'Cloud', color: '#f3efe9' },
  { id: 'sky', name: 'Sky', color: '#7ab8f0' },
];

// TUNE: all eye colours
export const KEEPER_EYE_COLORS: KeeperSwatch[] = [
  { id: 'cocoa', name: 'Cocoa', color: '#5a3a2a' },
  { id: 'plum', name: 'Plum', color: '#3b2a3f' },
  { id: 'sky', name: 'Sky', color: '#4f8fd6' },
  { id: 'leaf', name: 'Leaf', color: '#4fa36b' },
  { id: 'hazel', name: 'Hazel', color: '#9a7a3a' },
  { id: 'violet', name: 'Violet', color: '#8a62c4' },
];

// TUNE: all outfit palettes
export const KEEPER_OUTFITS: OutfitPalette[] = [
  {
    id: 'strawberry',
    name: 'Strawberry',
    top: '#ff8fab',
    bottom: '#fff1e6',
    shoes: '#c9577a',
    trim: '#ffffff',
  },
  {
    id: 'pumpkin',
    name: 'Pumpkin',
    top: '#ffa552',
    bottom: '#6b4a7a',
    shoes: '#4a3557',
    trim: '#ffe08a',
  },
  {
    id: 'meadow',
    name: 'Meadow',
    top: '#9fd88b',
    bottom: '#f7e3b5',
    shoes: '#6a9a5a',
    trim: '#ffffff',
  },
  {
    id: 'blueberry',
    name: 'Blueberry',
    top: '#7fa8f0',
    bottom: '#3f4f8c',
    shoes: '#2f3a66',
    trim: '#fff4c2',
  },
  {
    id: 'lavender',
    name: 'Lavender',
    top: '#c4a8f0',
    bottom: '#fbe7ff',
    shoes: '#7d5fb0',
    trim: '#ffffff',
  },
  {
    id: 'sunflower',
    name: 'Sunflower',
    top: '#ffd85a',
    bottom: '#4f7fc4',
    shoes: '#c96f3a',
    trim: '#ffffff',
  },
];

/** The top and back of the head; it sits up and back so the face stays clear under the fringe. */
const CAP: HairPiece = { shape: 'ellipsoid', at: [0, 0.28, 0.16], size: [1.08, 0.92, 1.08] };
/** It sits high enough that the brows always show under it, whatever the seeded jitter (#289). */
const FRINGE: HairPiece = {
  shape: 'ellipsoid',
  at: [0, 0.58, -0.5],
  size: [1.12, 0.5, 0.6],
  turn: [-25, 0, 0],
};
const ball = (x: number, y: number, z: number, d: number): HairPiece => ({
  shape: 'ellipsoid',
  at: [x, y, z],
  size: [d, d, d],
});

// TUNE: all hairstyles. Any Keeper can wear any of them (owner decision
// 2026-10-06); the picker lists them in this order, short to long.
export const KEEPER_HAIRSTYLES: KeeperHairstyle[] = [
  {
    id: 'spiky',
    name: 'Spiky',
    volume: { top: 1.45, width: 1.1 },
    pieces: [
      { shape: 'ellipsoid', at: [0, 0.3, 0.14], size: [1.06, 0.9, 1.06] },
      { shape: 'cone', at: [0, 1.12, 0.05], size: [0.42, 0.62, 0.42] },
      { shape: 'cone', at: [-0.5, 0.98, 0], size: [0.4, 0.56, 0.4], turn: [0, 0, 28] },
      { shape: 'cone', at: [0.5, 0.98, 0], size: [0.4, 0.56, 0.4], turn: [0, 0, -28] },
      { shape: 'cone', at: [0, 0.92, -0.5], size: [0.4, 0.5, 0.4], turn: [-32, 0, 0] },
      { shape: 'cone', at: [-0.4, 0.86, 0.52], size: [0.38, 0.5, 0.38], turn: [30, 0, 20] },
      { shape: 'cone', at: [0.4, 0.86, 0.52], size: [0.38, 0.5, 0.38], turn: [30, 0, -20] },
    ],
  },
  {
    // A neat short cut: close at the sides, a soft flat top, a little flick at the front.
    id: 'crew-cut',
    name: 'Crew Cut',
    volume: { top: 1.2, width: 1.06 },
    pieces: [
      { shape: 'ellipsoid', at: [0, 0.26, 0.18], size: [1.05, 0.9, 1.06] },
      { shape: 'ellipsoid', at: [0, 0.62, 0], size: [0.98, 0.66, 0.98] },
      { shape: 'ellipsoid', at: [0, 0.74, -0.58], size: [0.74, 0.3, 0.34], turn: [-40, 0, 0] },
    ],
  },
  {
    // Parted on one side and combed across, with a bit of lift by the part.
    id: 'side-part',
    name: 'Side Part',
    volume: { top: 1.22, width: 1.12 },
    pieces: [
      CAP,
      { shape: 'ellipsoid', at: [0.1, 0.62, -0.52], size: [1.14, 0.44, 0.58], turn: [-25, 0, -12] },
      { shape: 'ellipsoid', at: [-0.4, 0.7, -0.36], size: [0.5, 0.42, 0.56], turn: [-20, 0, 20] },
      { shape: 'ellipsoid', at: [0.6, 0.38, -0.5], size: [0.36, 0.5, 0.4], turn: [-10, 0, -30] },
    ],
  },
  {
    // Just rolled out of bed: soft tufts pointing every which way.
    id: 'messy-mop',
    name: 'Messy Mop',
    volume: { top: 1.32, width: 1.14 },
    pieces: [
      { shape: 'ellipsoid', at: [0, 0.3, 0.22], size: [1.1, 0.96, 1.1] },
      { shape: 'teardrop', at: [-0.42, 0.6, -0.7], size: [0.3, 0.42, 0.28], turn: [-150, 0, -30] },
      { shape: 'teardrop', at: [-0.02, 0.64, -0.76], size: [0.32, 0.44, 0.3], turn: [-155, 0, 12] },
      { shape: 'teardrop', at: [0.4, 0.6, -0.68], size: [0.3, 0.4, 0.28], turn: [-145, 0, 38] },
      { shape: 'teardrop', at: [0.12, 1.12, 0.06], size: [0.3, 0.46, 0.3], turn: [-15, 0, 28] },
      { shape: 'ellipsoid', at: [-0.42, 0.9, -0.2], size: [0.56, 0.4, 0.56], turn: [-10, 0, 25] },
      { shape: 'ellipsoid', at: [0.12, 1.0, 0.0], size: [0.58, 0.42, 0.6], turn: [8, 0, -12] },
      { shape: 'ellipsoid', at: [0.52, 0.86, 0.12], size: [0.54, 0.4, 0.56], turn: [0, 0, -30] },
      { shape: 'ellipsoid', at: [-0.3, 0.86, 0.5], size: [0.56, 0.42, 0.56], turn: [25, 0, 20] },
      { shape: 'teardrop', at: [-0.9, 0.3, 0.1], size: [0.34, 0.48, 0.34], turn: [0, 0, 150] },
      { shape: 'teardrop', at: [0.9, 0.3, 0.1], size: [0.34, 0.48, 0.34], turn: [0, 0, -150] },
      { shape: 'teardrop', at: [0, 0.2, 0.9], size: [0.4, 0.5, 0.36], turn: [150, 0, 0] },
    ],
  },
  {
    // Tight little curls close to the head (the big round `curly` is its own style).
    id: 'short-curls',
    name: 'Short Curls',
    volume: { top: 1.26, width: 1.14 },
    pieces: [
      { shape: 'ellipsoid', at: [0, 0.28, 0.2], size: [1.08, 0.94, 1.08] },
      ball(0, 1.04, 0.12, 0.34),
      ball(-0.44, 0.94, -0.16, 0.34),
      ball(0.44, 0.94, -0.16, 0.34),
      ball(-0.44, 0.92, 0.44, 0.34),
      ball(0.44, 0.92, 0.44, 0.34),
      ball(0, 0.84, -0.5, 0.32),
      ball(-0.38, 0.66, -0.68, 0.3),
      ball(0.38, 0.66, -0.68, 0.3),
      ball(-0.86, 0.56, 0.08, 0.32),
      ball(0.86, 0.56, 0.08, 0.32),
      ball(-0.72, 0.32, 0.62, 0.32),
      ball(0.72, 0.32, 0.62, 0.32),
      ball(0, 0.58, 0.88, 0.34),
    ],
  },
  {
    id: 'swoop',
    name: 'Swoop',
    volume: { top: 1.28, width: 1.1 },
    pieces: [
      CAP,
      FRINGE,
      { shape: 'teardrop', at: [-0.3, 0.74, -0.3], size: [0.75, 1.25, 0.6], turn: [-20, 0, 72] },
    ],
  },
  {
    id: 'curly',
    name: 'Curly',
    volume: { top: 1.38, width: 1.25 },
    pieces: [
      { shape: 'ellipsoid', at: [0, 0.3, 0.26], size: [1.14, 0.96, 1.12] },
      FRINGE,
      { shape: 'ellipsoid', at: [0, 1.08, 0.12], size: [0.55, 0.5, 0.55] },
      { shape: 'ellipsoid', at: [-0.6, 0.92, 0.1], size: [0.52, 0.5, 0.52] },
      { shape: 'ellipsoid', at: [0.6, 0.92, 0.1], size: [0.52, 0.5, 0.52] },
      ball(-0.95, 0.35, 0.25, 0.5),
      ball(0.95, 0.35, 0.25, 0.5),
      ball(-0.82, -0.25, 0.35, 0.46),
      ball(0.82, -0.25, 0.35, 0.46),
      { shape: 'ellipsoid', at: [0, 0.55, 0.85], size: [0.6, 0.55, 0.5] },
      { shape: 'ellipsoid', at: [-0.5, -0.1, 0.85], size: [0.52, 0.52, 0.48] },
      { shape: 'ellipsoid', at: [0.5, -0.1, 0.85], size: [0.52, 0.52, 0.48] },
    ],
  },
  {
    id: 'puff',
    name: 'Puff',
    volume: { top: 1.72, width: 1.34 },
    // Sits a little high so the brows show under it (#289).
    pieces: [{ shape: 'ellipsoid', at: [0, 0.58, 0.3], size: [1.34, 1.14, 1.24] }],
  },
  {
    id: 'bob',
    name: 'Bob',
    volume: { top: 1.2, width: 1.12 },
    pieces: [
      CAP,
      FRINGE,
      { shape: 'ellipsoid', at: [-0.86, -0.12, 0.12], size: [0.42, 1.1, 0.9] },
      { shape: 'ellipsoid', at: [0.86, -0.12, 0.12], size: [0.42, 1.1, 0.9] },
      { shape: 'ellipsoid', at: [0, -0.15, 0.42], size: [1.55, 1.05, 0.9] },
    ],
  },
  {
    id: 'bun',
    name: 'Bun',
    volume: { top: 1.2, width: 1.08 },
    pieces: [CAP, FRINGE, { shape: 'ellipsoid', at: [0, 1.15, 0.32], size: [0.72, 0.66, 0.72] }],
  },
  {
    id: 'pigtails',
    name: 'Pigtails',
    volume: { top: 1.2, width: 1.08 },
    pieces: [
      CAP,
      FRINGE,
      { shape: 'teardrop', at: [-1.12, -0.12, 0.22], size: [0.5, 0.95, 0.5], turn: [0, 0, -150] },
      { shape: 'teardrop', at: [1.12, -0.12, 0.22], size: [0.5, 0.95, 0.5], turn: [0, 0, 150] },
    ],
  },
  {
    id: 'long',
    name: 'Long',
    volume: { top: 1.2, width: 1.1 },
    pieces: [
      CAP,
      FRINGE,
      { shape: 'capsule', at: [0, -0.55, 0.5], size: [1.45, 1.5, 0.55] },
      { shape: 'capsule', at: [-0.88, -0.5, 0.05], size: [0.34, 1.2, 0.4] },
      { shape: 'capsule', at: [0.88, -0.5, 0.05], size: [0.34, 1.2, 0.4] },
    ],
  },
  // ── More styles (#372): short to long, as above ──────────────────────────
  {
    // Clippered close: just a thin cap, no fringe.
    id: 'buzz',
    name: 'Buzz Cut',
    volume: { top: 1.2, width: 1.04 },
    pieces: [{ shape: 'ellipsoid', at: [0, 0.3, 0.14], size: [1.03, 0.86, 1.03] }],
  },
  {
    // Short sides, a ridge of hair swept up the middle, front to back.
    id: 'faux-hawk',
    name: 'Faux Hawk',
    volume: { top: 1.5, width: 1.06 },
    pieces: [
      { shape: 'ellipsoid', at: [0, 0.3, 0.14], size: [1.04, 0.88, 1.04] },
      { shape: 'ellipsoid', at: [0, 0.98, -0.36], size: [0.34, 0.44, 0.46], turn: [-25, 0, 0] },
      { shape: 'ellipsoid', at: [0, 1.12, -0.02], size: [0.36, 0.52, 0.5] },
      { shape: 'ellipsoid', at: [0, 1.0, 0.36], size: [0.34, 0.46, 0.46], turn: [25, 0, 0] },
    ],
  },
  {
    // Parted in the middle, with two soft curtains framing the forehead.
    id: 'curtains',
    name: 'Curtains',
    volume: { top: 1.24, width: 1.14 },
    pieces: [
      CAP,
      { shape: 'ellipsoid', at: [-0.5, 0.64, -0.46], size: [0.6, 0.42, 0.5], turn: [-20, 0, 38] },
      { shape: 'ellipsoid', at: [0.5, 0.64, -0.46], size: [0.6, 0.42, 0.5], turn: [-20, 0, -38] },
      { shape: 'ellipsoid', at: [-0.9, 0.18, -0.1], size: [0.3, 0.6, 0.5] },
      { shape: 'ellipsoid', at: [0.9, 0.18, -0.1], size: [0.3, 0.6, 0.5] },
    ],
  },
  {
    // Little twists sticking up all over, like springs.
    id: 'twists',
    name: 'Short Twists',
    volume: { top: 1.4, width: 1.12 },
    pieces: [
      { shape: 'ellipsoid', at: [0, 0.28, 0.16], size: [1.06, 0.9, 1.06] },
      { shape: 'capsule', at: [0, 1.08, 0.08], size: [0.2, 0.4, 0.2] },
      { shape: 'capsule', at: [-0.46, 0.98, -0.1], size: [0.2, 0.4, 0.2], turn: [0, 0, 24] },
      { shape: 'capsule', at: [0.46, 0.98, -0.1], size: [0.2, 0.4, 0.2], turn: [0, 0, -24] },
      { shape: 'capsule', at: [-0.46, 0.96, 0.4], size: [0.2, 0.4, 0.2], turn: [20, 0, 22] },
      { shape: 'capsule', at: [0.46, 0.96, 0.4], size: [0.2, 0.4, 0.2], turn: [20, 0, -22] },
      { shape: 'capsule', at: [0, 0.86, 0.66], size: [0.2, 0.4, 0.2], turn: [38, 0, 0] },
      { shape: 'capsule', at: [-0.84, 0.62, 0.1], size: [0.2, 0.4, 0.2], turn: [0, 0, 55] },
      { shape: 'capsule', at: [0.84, 0.62, 0.1], size: [0.2, 0.4, 0.2], turn: [0, 0, -55] },
      { shape: 'capsule', at: [0, 0.96, -0.54], size: [0.2, 0.36, 0.2], turn: [-34, 0, 0] },
    ],
  },
  {
    // Layers that fall over the ears and neck, a bit unruly.
    id: 'shaggy',
    name: 'Shaggy',
    volume: { top: 1.26, width: 1.18 },
    pieces: [
      { shape: 'ellipsoid', at: [0, 0.3, 0.2], size: [1.12, 0.94, 1.1] },
      { shape: 'ellipsoid', at: [-0.38, 0.7, -0.5], size: [0.6, 0.38, 0.48], turn: [-25, 0, 28] },
      { shape: 'ellipsoid', at: [0.4, 0.68, -0.5], size: [0.6, 0.38, 0.48], turn: [-25, 0, -22] },
      { shape: 'ellipsoid', at: [-0.94, 0.0, 0.1], size: [0.4, 0.8, 0.54], turn: [0, 0, -8] },
      { shape: 'ellipsoid', at: [0.94, 0.0, 0.1], size: [0.4, 0.8, 0.54], turn: [0, 0, 8] },
      { shape: 'ellipsoid', at: [-0.8, -0.5, 0.3], size: [0.34, 0.5, 0.4], turn: [0, 0, -14] },
      { shape: 'ellipsoid', at: [0.8, -0.5, 0.3], size: [0.34, 0.5, 0.4], turn: [0, 0, 14] },
      { shape: 'ellipsoid', at: [0, -0.2, 0.9], size: [1.1, 0.9, 0.46] },
    ],
  },
  {
    // Two bunches up high, at the top corners of the head.
    id: 'high-pigtails',
    name: 'High Pigtails',
    volume: { top: 1.24, width: 1.1 },
    pieces: [
      CAP,
      FRINGE,
      ball(-0.78, 0.86, 0.3, 0.3),
      ball(0.78, 0.86, 0.3, 0.3),
      { shape: 'teardrop', at: [-1.02, 0.5, 0.34], size: [0.46, 0.9, 0.46], turn: [0, 0, -155] },
      { shape: 'teardrop', at: [1.02, 0.5, 0.34], size: [0.46, 0.9, 0.46], turn: [0, 0, 155] },
    ],
  },
  {
    // Two bunches low by the neck, hanging straight down.
    id: 'low-pigtails',
    name: 'Low Pigtails',
    volume: { top: 1.2, width: 1.08 },
    pieces: [
      CAP,
      FRINGE,
      ball(-0.82, -0.04, 0.32, 0.26),
      ball(0.82, -0.04, 0.32, 0.26),
      { shape: 'teardrop', at: [-0.88, -0.62, 0.34], size: [0.44, 0.88, 0.44], turn: [0, 0, 180] },
      { shape: 'teardrop', at: [0.88, -0.62, 0.34], size: [0.44, 0.88, 0.44], turn: [0, 0, 180] },
    ],
  },
  {
    // Two bouncy bunches of curls.
    id: 'curly-pigtails',
    name: 'Curly Pigtails',
    volume: { top: 1.22, width: 1.1 },
    pieces: [
      CAP,
      FRINGE,
      ball(-1.02, 0.46, 0.3, 0.46),
      ball(1.02, 0.46, 0.3, 0.46),
      ball(-1.1, -0.04, 0.36, 0.42),
      ball(1.1, -0.04, 0.36, 0.42),
      ball(-1.04, -0.5, 0.4, 0.38),
      ball(1.04, -0.5, 0.4, 0.38),
    ],
  },
  {
    // One braid over the right shoulder, tied with a little bobble.
    id: 'braid',
    name: 'Braid',
    volume: { top: 1.2, width: 1.08 },
    pieces: [
      CAP,
      FRINGE,
      { shape: 'ellipsoid', at: [0.88, -0.1, 0.1], size: [0.4, 0.46, 0.36] },
      { shape: 'ellipsoid', at: [0.9, -0.52, -0.08], size: [0.36, 0.44, 0.34] },
      { shape: 'ellipsoid', at: [0.86, -0.94, -0.3], size: [0.34, 0.42, 0.32] },
      { shape: 'ellipsoid', at: [0.8, -1.34, -0.5], size: [0.32, 0.4, 0.3] },
      ball(0.76, -1.64, -0.6, 0.2),
    ],
  },
  {
    // Two braids over the shoulders, one each side.
    id: 'twin-braids',
    name: 'Twin Braids',
    volume: { top: 1.2, width: 1.1 },
    pieces: [
      CAP,
      FRINGE,
      { shape: 'ellipsoid', at: [-0.88, -0.1, 0.1], size: [0.36, 0.42, 0.34] },
      { shape: 'ellipsoid', at: [0.88, -0.1, 0.1], size: [0.36, 0.42, 0.34] },
      { shape: 'ellipsoid', at: [-0.9, -0.5, -0.08], size: [0.34, 0.4, 0.32] },
      { shape: 'ellipsoid', at: [0.9, -0.5, -0.08], size: [0.34, 0.4, 0.32] },
      { shape: 'ellipsoid', at: [-0.86, -0.9, -0.3], size: [0.32, 0.38, 0.3] },
      { shape: 'ellipsoid', at: [0.86, -0.9, -0.3], size: [0.32, 0.38, 0.3] },
      { shape: 'ellipsoid', at: [-0.8, -1.28, -0.5], size: [0.3, 0.36, 0.28] },
      { shape: 'ellipsoid', at: [0.8, -1.28, -0.5], size: [0.3, 0.36, 0.28] },
      ball(-0.76, -1.56, -0.6, 0.2),
      ball(0.76, -1.56, -0.6, 0.2),
    ],
  },
  {
    // Tied high on the back of the head, swinging out behind.
    id: 'high-ponytail',
    name: 'High Ponytail',
    volume: { top: 1.3, width: 1.06 },
    pieces: [
      CAP,
      FRINGE,
      ball(0, 0.86, 0.74, 0.24),
      { shape: 'teardrop', at: [0, 0.5, 1.28], size: [0.5, 1.05, 0.46], turn: [-28, 0, 180] },
    ],
  },
  {
    // Tied low at the nape, then brought round over the right shoulder.
    id: 'low-ponytail',
    name: 'Low Ponytail',
    volume: { top: 1.2, width: 1.06 },
    pieces: [
      CAP,
      FRINGE,
      ball(0.5, -0.1, 0.8, 0.24),
      { shape: 'teardrop', at: [0.88, -0.86, -0.1], size: [0.56, 1.15, 0.5], turn: [-8, 0, 180] },
    ],
  },
  {
    // Swept to one side and over the shoulder.
    id: 'side-ponytail',
    name: 'Side Ponytail',
    volume: { top: 1.22, width: 1.12 },
    pieces: [
      CAP,
      FRINGE,
      ball(0.8, 0.36, 0.34, 0.26),
      { shape: 'teardrop', at: [1.12, -0.24, 0.4], size: [0.52, 1.05, 0.46], turn: [0, 0, 165] },
    ],
  },
  {
    // Long and loose with a centre parting: the front stays clear of the face.
    id: 'long-loose',
    name: 'Long Loose',
    volume: { top: 1.24, width: 1.1 },
    pieces: [
      { shape: 'ellipsoid', at: [0, 0.3, 0.24], size: [1.1, 0.92, 1.08] },
      { shape: 'ellipsoid', at: [-0.56, 0.74, -0.38], size: [0.5, 0.34, 0.46], turn: [-20, 0, 34] },
      { shape: 'ellipsoid', at: [0.56, 0.74, -0.38], size: [0.5, 0.34, 0.46], turn: [-20, 0, -34] },
      { shape: 'capsule', at: [0, -0.62, 0.52], size: [1.5, 1.6, 0.55] },
      { shape: 'capsule', at: [-0.92, -0.5, 0.08], size: [0.32, 1.3, 0.4] },
      { shape: 'capsule', at: [0.92, -0.5, 0.08], size: [0.32, 1.3, 0.4] },
    ],
  },
];

// TUNE: all skin tones (owner 2026-10-08, #289): ten, light to deep; Hazel's is
// the deepest. Shown with no names.
export const KEEPER_SKIN_TONES: KeeperSkinTone[] = [
  { id: 'tone-1', color: '#fde3cf' },
  { id: 'tone-2', color: '#f3d6c6' },
  { id: 'tone-3', color: '#f6cfae' },
  { id: 'tone-4', color: '#eab48c' },
  { id: 'tone-5', color: '#c8a27c' },
  { id: 'tone-6', color: '#d39a6c' },
  { id: 'tone-7', color: '#b97c52' },
  { id: 'tone-8', color: '#a5683f' },
  { id: 'tone-9', color: '#9a603b' },
  { id: 'tone-10', color: '#7a4a2c' },
];

// TUNE: brow and mouth lines (#289). All thin, dark lines on every skin tone.
export const KEEPER_BROWS: KeeperFaceLine[] = [
  { id: 'arched', name: 'Arched', shape: 'arc', size: [0.13, 0.03], depth: 1, roll: 180 },
  { id: 'straight', name: 'Straight', shape: 'capsule', size: [0.11, 0.022], depth: 1, roll: 0 },
  { id: 'soft', name: 'Soft', shape: 'arc', size: [0.09, 0.04], depth: 1.4, roll: 180 },
];

export const KEEPER_MOUTHS: KeeperFaceLine[] = [
  { id: 'smile', name: 'Smile', shape: 'arc', size: [0.15, 0.06], depth: 1.6, roll: 0 },
  {
    id: 'small-smile',
    name: 'Small smile',
    shape: 'arc',
    size: [0.09, 0.045],
    depth: 1.6,
    roll: 0,
  },
  {
    id: 'big-smile',
    name: 'Big smile',
    shape: 'arc',
    size: [0.19, 0.05],
    depth: 1.6,
    roll: 0,
    lift: -0.01,
  },
];

// TUNE: face extras (#289). Off unless picked; every Keeper can pick any.
export const KEEPER_FACE_EXTRAS: KeeperFaceExtra[] = [
  {
    id: 'blush',
    name: 'Blush',
    pieces: [
      {
        shape: 'ellipsoid',
        at: [0.3, -0.14],
        size: [0.15, 0.08, 0.035],
        color: '#ff9db5',
        mirror: true,
      },
    ],
  },
  {
    id: 'freckles',
    name: 'Freckles',
    pieces: [
      {
        shape: 'ellipsoid',
        at: [0.265, -0.09],
        size: [0.024, 0.024, 0.012],
        color: '#b0705a',
        mirror: true,
      },
      {
        shape: 'ellipsoid',
        at: [0.3, -0.07],
        size: [0.024, 0.024, 0.012],
        color: '#b0705a',
        mirror: true,
      },
      {
        shape: 'ellipsoid',
        at: [0.335, -0.09],
        size: [0.024, 0.024, 0.012],
        color: '#b0705a',
        mirror: true,
      },
    ],
  },
  {
    id: 'lashes',
    name: 'Lashes',
    pieces: [
      {
        shape: 'capsule',
        at: [0.255, 0.03],
        size: [0.016, 0.05, 0.012],
        color: 'line',
        roll: 40,
        mirror: true,
      },
    ],
  },
  {
    id: 'heart-sticker',
    name: 'Heart sticker',
    pieces: [
      { shape: 'ellipsoid', at: [0.282, -0.108], size: [0.04, 0.04, 0.012], color: '#ff6f91' },
      { shape: 'ellipsoid', at: [0.318, -0.108], size: [0.04, 0.04, 0.012], color: '#ff6f91' },
      { shape: 'capsule', at: [0.3, -0.132], size: [0.03, 0.045, 0.012], color: '#ff6f91' },
    ],
  },
];

// TUNE: face shapes (#372): each starting look's head, so its id is the
// base's and a Keeper saved before faces were a choice looks the same.
// Listed round to long. `head` is its size (blended with the body's own),
// `headShape` its width over height.
export const KEEPER_FACES: KeeperFaceShape[] = [
  { id: 'acorn', name: 'Moon', head: 0.58, headShape: 1.14 },
  { id: 'bramble', name: 'Muffin', head: 0.48, headShape: 1.12 },
  { id: 'pip', name: 'Bubble', head: 0.56, headShape: 1.1 },
  { id: 'wren', name: 'Button', head: 0.52, headShape: 1.08 },
  { id: 'clover', name: 'Apple', head: 0.5, headShape: 1.05 },
  { id: 'sunny', name: 'Peach', head: 0.5, headShape: 1.04 },
  { id: 'basil', name: 'Plum', head: 0.47, headShape: 1.02 },
  { id: 'moss', name: 'Pebble', head: 0.49, headShape: 1 },
  { id: 'rowan', name: 'Almond', head: 0.43, headShape: 0.96 },
  { id: 'maple', name: 'Oval', head: 0.44, headShape: 0.95 },
  { id: 'hazel', name: 'Bean', head: 0.45, headShape: 0.92 },
  { id: 'juniper', name: 'Egg', head: 0.5, headShape: 0.88 },
];

// TUNE: body types (#372): each starting look's body (same id, so saved
// Keepers look the same) plus two new ones, Lanky and Sturdy. Listed small
// to tall. `head` is the head size the body is drawn for.
export const KEEPER_BODIES: KeeperBodyType[] = [
  {
    id: 'acorn',
    name: 'Tiny',
    height: 0.84,
    head: 0.58,
    torsoWidth: 0.36,
    torsoHeight: 0.19,
    roundness: 1,
    legs: 0.11,
    arms: 0.15,
    limbs: 0.1,
  },
  {
    id: 'pip',
    name: 'Little',
    height: 0.88,
    head: 0.56,
    torsoWidth: 0.28,
    torsoHeight: 0.2,
    roundness: 0.8,
    legs: 0.13,
    arms: 0.17,
    limbs: 0.08,
  },
  {
    id: 'wren',
    name: 'Small',
    height: 0.9,
    head: 0.52,
    torsoWidth: 0.26,
    torsoHeight: 0.21,
    roundness: 0.6,
    legs: 0.14,
    arms: 0.18,
    limbs: 0.075,
  },
  {
    id: 'juniper',
    name: 'Slim',
    height: 0.94,
    head: 0.5,
    torsoWidth: 0.24,
    torsoHeight: 0.22,
    roundness: 0.55,
    legs: 0.17,
    arms: 0.19,
    limbs: 0.065,
  },
  {
    id: 'sunny',
    name: 'Cozy',
    height: 0.96,
    head: 0.5,
    torsoWidth: 0.32,
    torsoHeight: 0.23,
    roundness: 0.7,
    legs: 0.15,
    arms: 0.19,
    limbs: 0.085,
  },
  {
    id: 'clover',
    name: 'Medium',
    height: 1,
    head: 0.5,
    torsoWidth: 0.3,
    torsoHeight: 0.24,
    roundness: 0.5,
    legs: 0.16,
    arms: 0.2,
    limbs: 0.08,
  },
  {
    id: 'moss',
    name: 'Sporty',
    height: 1.02,
    head: 0.49,
    torsoWidth: 0.31,
    torsoHeight: 0.25,
    roundness: 0.45,
    legs: 0.17,
    arms: 0.21,
    limbs: 0.09,
  },
  {
    id: 'basil',
    name: 'Broad',
    height: 1.04,
    head: 0.47,
    torsoWidth: 0.38,
    torsoHeight: 0.26,
    roundness: 0.3,
    legs: 0.16,
    arms: 0.22,
    limbs: 0.1,
  },
  {
    id: 'bramble',
    name: 'Cuddly',
    height: 1.08,
    head: 0.48,
    torsoWidth: 0.4,
    torsoHeight: 0.28,
    roundness: 0.95,
    legs: 0.15,
    arms: 0.21,
    limbs: 0.11,
  },
  {
    // New (#372): tall and strong, broad without being round.
    id: 'sturdy',
    name: 'Sturdy',
    height: 1.1,
    head: 0.46,
    torsoWidth: 0.42,
    torsoHeight: 0.29,
    roundness: 0.4,
    legs: 0.17,
    arms: 0.23,
    limbs: 0.12,
  },
  {
    id: 'rowan',
    name: 'Lean',
    height: 1.12,
    head: 0.43,
    torsoWidth: 0.25,
    torsoHeight: 0.29,
    roundness: 0.2,
    legs: 0.21,
    arms: 0.25,
    limbs: 0.07,
  },
  {
    id: 'maple',
    name: 'Tall',
    height: 1.12,
    head: 0.44,
    torsoWidth: 0.26,
    torsoHeight: 0.27,
    roundness: 0.25,
    legs: 0.21,
    arms: 0.24,
    limbs: 0.07,
  },
  {
    id: 'hazel',
    name: 'Very tall',
    height: 1.15,
    head: 0.45,
    torsoWidth: 0.27,
    torsoHeight: 0.28,
    roundness: 0.35,
    legs: 0.22,
    arms: 0.25,
    limbs: 0.075,
  },
  {
    // New (#372): the tallest, with long legs and arms.
    id: 'lanky',
    name: 'Lanky',
    height: 1.2,
    head: 0.42,
    torsoWidth: 0.23,
    torsoHeight: 0.3,
    roundness: 0.15,
    legs: 0.24,
    arms: 0.28,
    limbs: 0.065,
  },
];

// TUNE: all bases (starting looks). Each one's face and body are the face
// and body with its id (#372). Skin tones run light to deep.
export const KEEPER_BASES: KeeperBase[] = [
  {
    id: 'pip',
    name: 'Pip',
    skinTone: 'tone-1',
    face: { eyes: 'round', brows: 'arched', mouth: 'smile' },
    hairstyle: 'spiky',
    hairColor: 'honey',
    eyeColor: 'sky',
    outfit: 'sunflower',
  },
  {
    id: 'clover',
    name: 'Clover',
    skinTone: 'tone-3',
    face: { eyes: 'oval', brows: 'arched', mouth: 'smile' },
    hairstyle: 'bob',
    hairColor: 'ginger',
    eyeColor: 'leaf',
    outfit: 'meadow',
  },
  {
    id: 'rowan',
    name: 'Rowan',
    skinTone: 'tone-10',
    face: { eyes: 'oval', brows: 'arched', mouth: 'smile' },
    hairstyle: 'crew-cut',
    hairColor: 'midnight',
    eyeColor: 'hazel',
    outfit: 'sunflower',
  },
  {
    id: 'maple',
    name: 'Maple',
    skinTone: 'tone-4',
    face: { eyes: 'happy', brows: 'arched', mouth: 'smile' },
    hairstyle: 'long',
    hairColor: 'cocoa',
    eyeColor: 'cocoa',
    outfit: 'lavender',
  },
  {
    id: 'basil',
    name: 'Basil',
    skinTone: 'tone-5',
    face: { eyes: 'happy', brows: 'arched', mouth: 'smile' },
    hairstyle: 'side-part',
    hairColor: 'cocoa',
    eyeColor: 'cocoa',
    outfit: 'meadow',
  },
  {
    id: 'wren',
    name: 'Wren',
    skinTone: 'tone-6',
    face: { eyes: 'round', brows: 'arched', mouth: 'smile' },
    hairstyle: 'pigtails',
    hairColor: 'berry',
    eyeColor: 'hazel',
    outfit: 'strawberry',
  },
  {
    id: 'acorn',
    name: 'Acorn',
    skinTone: 'tone-8',
    face: { eyes: 'round', brows: 'arched', mouth: 'smile' },
    hairstyle: 'short-curls',
    hairColor: 'midnight',
    eyeColor: 'cocoa',
    outfit: 'pumpkin',
  },
  {
    id: 'bramble',
    name: 'Bramble',
    skinTone: 'tone-7',
    face: { eyes: 'oval', brows: 'arched', mouth: 'smile' },
    hairstyle: 'curly',
    hairColor: 'midnight',
    eyeColor: 'cocoa',
    outfit: 'pumpkin',
  },
  {
    id: 'sunny',
    name: 'Sunny',
    skinTone: 'tone-9',
    face: { eyes: 'happy', brows: 'arched', mouth: 'smile' },
    hairstyle: 'puff',
    hairColor: 'midnight',
    eyeColor: 'plum',
    outfit: 'strawberry',
  },
  {
    id: 'juniper',
    name: 'Juniper',
    skinTone: 'tone-2',
    face: { eyes: 'sleepy', brows: 'arched', mouth: 'smile' },
    hairstyle: 'messy-mop',
    hairColor: 'sky',
    eyeColor: 'sky',
    outfit: 'lavender',
  },
  {
    id: 'hazel',
    name: 'Hazel',
    skinTone: 'tone-10',
    face: { eyes: 'sleepy', brows: 'arched', mouth: 'smile' },
    hairstyle: 'bun',
    hairColor: 'lilac',
    eyeColor: 'violet',
    outfit: 'blueberry',
  },
  {
    id: 'moss',
    name: 'Moss',
    skinTone: 'tone-9',
    face: { eyes: 'round', brows: 'arched', mouth: 'smile' },
    hairstyle: 'swoop',
    hairColor: 'mint',
    eyeColor: 'leaf',
    outfit: 'blueberry',
  },
];

/** Every Keeper table, checked by `checkKeeperData` in tests. */
export const KEEPER_DATA: KeeperData = {
  bases: KEEPER_BASES,
  faces: KEEPER_FACES,
  bodies: KEEPER_BODIES,
  skinTones: KEEPER_SKIN_TONES,
  brows: KEEPER_BROWS,
  mouths: KEEPER_MOUTHS,
  faceExtras: KEEPER_FACE_EXTRAS,
  hairstyles: KEEPER_HAIRSTYLES,
  hairColors: KEEPER_HAIR_COLORS,
  eyeColors: KEEPER_EYE_COLORS,
  outfits: KEEPER_OUTFITS,
};
